import { useState } from "react"
import { Link, useNavigate } from "../lib/router"
import { Eye, EyeOff, Waves, ChevronLeft } from "lucide-react"
import { BRAND } from "../lib/brand"
import indoorPool from "../assets/indoor-pool.webp"
import { useLanguage } from "../lib/LanguageContext"
import LangToggle from "../components/LangToggle"

// Auth backend (register/login/me) — override via .env for non-local setups.
const API = process.env.REACT_APP_AUTH_API_URL || "http://localhost:8001"

export default function Login() {
  const navigate = useNavigate()
  const [form, setForm] = useState({ email: "", password: "" })
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)
  const { t } = useLanguage()

  const handleChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }))
    if (error) setError("")
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!form.email || !form.password) {
      setError(t("login_err_fill"))
      return
    }
    setLoading(true)
    try {
      const res = await fetch(`${API}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: form.email, password: form.password }),
      })

      if (res.status === 403) {
        const data = await res.json()

        if (data.detail === "rejected") {
          setError(t("login_err_rejected"))
          return
        }
      }

      if (res.status === 401) {
        setError(t("login_err_credentials"))
        return
      }

      if (!res.ok) {
        setError(t("login_err_generic"))
        return
      }

      const data = await res.json()
      // Store the JWT token for subsequent authenticated requests
      localStorage.setItem("token", data.access_token)
      localStorage.setItem("role", data.role)
      localStorage.setItem("name", data.name)
      if (data.club_name) {
        localStorage.setItem("clubName", data.club_name)
      } else {
        localStorage.removeItem("clubName")
      }

      const role = data.role
      const status = data.status || "approved"

      if (status === "pending") {
        navigate("/verification-pending")
        return
      }

      if (role === "admin") {
        navigate("/admin/dashboard")
      } else if (role === "club") {
        navigate("/club-admin/dashboard")
      } else if (role === "coach") {
        navigate("/coach/dashboard")
      } else {
        navigate("/swimmer/dashboard")
      }
    } catch {
      setError(t("login_err_network"))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex min-h-screen bg-ink-900">
      {/* Mobile Back to Home (Hidden on md+ screens) */}
      <Link
        to="/"
        className="md:hidden absolute left-5 top-6 z-50 flex items-center gap-2 border border-white/20 bg-ink-800/80 px-4 py-2 text-sm font-semibold text-paper-0 transition-colors hover:border-white/40 sm:left-8 sm:top-8"
      >
        <ChevronLeft className="h-4 w-4" />
        {t("login_home")}
      </Link>

      {/* Left side: Vibrant Hero Image */}
      <div className="hidden md:block relative w-[55%] overflow-hidden bg-ink-950">
        <div
          className="absolute inset-0 bg-cover transform hover:scale-105 transition-transform duration-[20s] ease-out"
          style={{
            backgroundImage: `url(${indoorPool})`,
            backgroundPosition: "center 30%",
          }}
          aria-hidden="true"
        />
        {/* Gradients for text readability & blending */}
        <div className="absolute inset-0 bg-gradient-to-t from-ink-900 via-ink-900/40 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink-900/50 via-transparent to-ink-900" />
        {/* Desktop Top Left: Logo */}
        <img
          src="/vietnam_aquatics.png"
          alt="Logo"
          className="absolute left-8 top-8 z-20 h-8 w-auto rounded-lg bg-white/95 p-1.5 shadow-sm"
          onError={(e) => { e.currentTarget.style.display = "none" }}
        />

        {/* Desktop Top Right: Home Button */}
        <Link
          to="/"
          className="absolute right-8 top-8 z-20 flex items-center justify-center border border-white/20 bg-ink-900/40 px-5 py-2 text-sm font-medium text-white transition-colors hover:border-white/40"
        >
          {t("login_home")}
        </Link>
        
        <div className="absolute bottom-16 left-16 right-16">
          <h2 className="font-display text-4xl font-bold text-white mb-4 leading-tight">
            {t("login_hero_h2")} <br/>
            <span className="text-gold-400">{t("login_hero_span")}</span>
          </h2>
          <p className="text-lg text-mist-300 max-w-lg">
            {t("login_hero_p")}
          </p>
        </div>
      </div>

      {/* Right side: Login Form */}
      <div className="flex flex-1 flex-col items-center justify-center px-4 sm:px-6 lg:px-20 xl:px-24">
        <div className="w-full max-w-[400px]">
        {/* Language toggle */}
        <div className="flex justify-end mb-4">
          <LangToggle />
        </div>

        <div className="mb-9 flex flex-col items-center gap-3 text-center md:hidden">
          <a
            href="/"
            className="mb-1 flex items-center gap-3 text-paper-0 transition-opacity hover:opacity-80"
            aria-label="Go to homepage"
          >
            <img
              src="/vietnam_aquatics.png"
              alt={`${BRAND.name} logo`}
              className="h-16 w-auto object-contain"
              onError={(e) => {
                e.currentTarget.style.display = "none"
              }}
            />
          </a>
          <p className="text-xs font-semibold uppercase tracking-[.18em] text-gold-400">
            {BRAND.poweredBy}
          </p>
        </div>

        {/* Card */}
        <div
          className="relative overflow-hidden border border-white/10 bg-ink-800/60 p-8 shadow-2xl backdrop-blur-sm"
          style={{ boxShadow: "0 0 0 1px rgba(217,179,108,.06), 0 32px 64px -16px rgba(0,0,0,.6)" }}
        >
          {/* Gold top accent */}
          <div
            className="pointer-events-none absolute inset-x-0 top-0 h-[2px]"
            style={{ background: "linear-gradient(90deg, transparent, #D9B36C 50%, transparent)" }}
            aria-hidden="true"
          />

          <div className="mb-7">
            <h1 className="font-display text-[1.75rem] font-bold leading-tight tracking-[-0.01em] text-paper-0">
              {t("login_title")}
            </h1>
            <p className="mt-1.5 text-sm font-medium text-mist-300">
              {t("login_subtitle")}
            </p>
          </div>

          <form id="login-form" onSubmit={handleSubmit} noValidate>
            <div className="flex flex-col gap-5">
              {/* Email */}
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="login-email"
                  className="text-xs font-semibold uppercase tracking-[.14em] text-fog-200"
                >
                  {t("login_email")}
                </label>
                <input
                  id="login-email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="you@example.com"
                  className="h-11 w-full border border-white/20 bg-ink-700/50 px-4 text-paper-0 placeholder:text-mist-400 transition-colors focus:border-gold-400/60 focus:outline-none focus:ring-1 focus:ring-gold-400/30"
                  aria-required="true"
                  aria-invalid={!!error}
                  aria-describedby={error ? "login-error" : undefined}
                />
              </div>

              {/* Password */}
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <label
                    htmlFor="login-password"
                    className="text-xs font-semibold uppercase tracking-[.14em] text-fog-200"
                  >
                    {t("login_password")}
                  </label>

                </div>
                <div className="relative">
                  <input
                    id="login-password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    value={form.password}
                    onChange={handleChange}
                    placeholder="••••••••"
                    className="h-11 w-full border border-white/20 bg-ink-700/50 px-4 pr-11 text-paper-0 placeholder:text-mist-400 transition-colors focus:border-gold-400/60 focus:outline-none focus:ring-1 focus:ring-gold-400/30"
                    aria-required="true"
                    aria-invalid={!!error}
                    aria-describedby={error ? "login-error" : undefined}
                  />
                  <button
                    type="button"
                    id="toggle-password-visibility"
                    className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-mist-300 transition-colors hover:text-paper-0"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? t("login_hide_password") : t("login_show_password")}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" aria-hidden="true" />
                    ) : (
                      <Eye className="h-4 w-4" aria-hidden="true" />
                    )}
                  </button>
                </div>
              </div>

              {/* Error message */}
              {error && (
                <div
                  id="login-error"
                  role="alert"
                  className="flex items-start gap-3 border border-red-500/30 bg-red-500/10 px-4 py-3"
                >
                  <svg
                    className="mt-0.5 h-4 w-4 shrink-0 text-red-400"
                    viewBox="0 0 16 16"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path d="M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1zm0 10.5a.875.875 0 1 1 0-1.75.875.875 0 0 1 0 1.75zM7.25 5.5a.75.75 0 0 1 1.5 0v3a.75.75 0 0 1-1.5 0v-3z" />
                  </svg>
                  <p className="text-sm font-medium text-red-300">{error}</p>
                </div>
              )}


              {/* Submit */}
              <button
                id="login-submit"
                type="submit"
                disabled={loading}
                className="relative mt-1 h-11 w-full overflow-hidden bg-gold-500 text-sm font-semibold tracking-[.04em] text-ink-900 transition-colors hover:bg-gold-400 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg
                      className="h-4 w-4 animate-spin"
                      viewBox="0 0 24 24"
                      fill="none"
                      aria-hidden="true"
                    >
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                    {t("login_submitting")}
                  </span>
                ) : (
                  t("login_submit")
                )}
              </button>
            </div>
          </form>

          {/* Divider */}
          <div className="my-6 flex items-center gap-3">
            <div className="h-px flex-1 bg-white/10" />
            <span className="text-xs font-medium text-mist-400">{t("login_or")}</span>
            <div className="h-px flex-1 bg-white/10" />
          </div>

          {/* Sign up link */}
          <p className="text-center text-sm font-medium text-mist-300">
            {t("login_no_account")}{" "}
            <Link
              to="/signup"
              id="go-to-signup"
              className="font-semibold text-gold-400 transition-colors hover:text-gold-300"
            >
              {t("login_create")}
            </Link>
          </p>
        </div>

        {/* Footer note */}
        <p className="mt-8 text-center text-[11px] font-medium text-mist-400">
          <Waves className="mb-0.5 mr-1 inline h-3 w-3" aria-hidden="true" />
          {BRAND.copyright}
        </p>
        </div>
      </div>
    </div>
  )
}
